import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/shadcn/navigation-menu';
import { useOrganizationContext } from '@/contexts/organization-context';
import {
  hasAdminRole,
  hasGrantManagerRole,
  hasSocialMediaManagerRole,
} from '@/lib/auth-service';
import { cn } from '@/lib/utils';
import { Separator } from '@radix-ui/react-dropdown-menu';
import { env } from 'next-runtime-env';
import Link from 'next/link';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import React from 'react';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';

const WEBSITE_DOMAIN = env('WEBSITE');

export const ListItem = React.forwardRef<
  React.ElementRef<'a'>,
  React.ComponentPropsWithoutRef<'a'>
>(({ className, title, children, href, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          href={href ?? ''}
          ref={ref}
          className={cn(
            'block select-none space-y-1 whitespace-nowrap rounded-md p-3 text-base leading-none text-gray-settings no-underline outline-none transition-colors hover:bg-accent hover:text-foreground',
            className
          )}
          {...props}
        >
          <div className='inline-block text-base font-medium text-[#333]'>
            {title}
          </div>
        </Link>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = 'ListItem';

export default function NavBar() {
  const session = useSessionContext();
  const { organizationId, isGrantUser, isMaggieUser } =
    useOrganizationContext();
  const isMaggieDashboardEnabled =
    useFeatureFlagEnabled('maggie-dashboard') === true;
  const isGrantDashboardEnabled =
    useFeatureFlagEnabled('grant-dashboard') === true;
  const isGrantDashboardVeeEnabled =
    useFeatureFlagEnabled('grant-dashboard-orgs') === true;

  if (session.loading || !session.doesSessionExist) {
    return null;
  }

  return (
    <NavigationMenu>
      <NavigationMenuList className='mb-3'>
        {isMaggieUser &&
          hasSocialMediaManagerRole(
            session.accessTokenPayload['st-role']?.v
          ) && (
            <NavigationMenu>
              <NavigationMenuItem>
                <NavigationMenuTrigger
                  className='text-base text-gray-settings hover:text-foreground focus:bg-white focus:text-gray-settings'
                  hideIcon={true}
                  onPointerMove={(event) => event.preventDefault()}
                  onPointerLeave={(event) => event.preventDefault()}
                >
                  Social Media
                </NavigationMenuTrigger>
                <NavigationMenuContent
                  onPointerEnter={(event) => event.preventDefault()}
                  onPointerLeave={(event) => event.preventDefault()}
                >
                  <ul className='flex flex-col'>
                    {hasAdminRole(session.accessTokenPayload['st-role']?.v) && (
                      <ListItem
                        href={`${WEBSITE_DOMAIN}/maggie/source-content-management/org/${organizationId}`}
                        title='Source Content Management'
                      />
                    )}
                    {hasAdminRole(session.accessTokenPayload['st-role']?.v) && (
                      <>
                        <Separator className='m-0 h-[1px] bg-muted' />
                        <ListItem
                          href={`${WEBSITE_DOMAIN}/maggie/posts-overview/org/${organizationId}`}
                          title='Posts Overview'
                        />
                      </>
                    )}
                    {hasAdminRole(session.accessTokenPayload['st-role']?.v) && (
                      <>
                        <Separator className='m-0 h-[1px] bg-muted' />
                        <ListItem
                          href={`${WEBSITE_DOMAIN}/maggie/events-calendar/org/${organizationId}`}
                          title='Organization Event Calendar'
                        />
                      </>
                    )}
                    {hasSocialMediaManagerRole(
                      session.accessTokenPayload['st-role']?.v
                    ) && (
                      <>
                        <Separator className='m-0 h-[1px] bg-muted' />
                        <ListItem
                          href={
                            isMaggieDashboardEnabled
                              ? `/maggie?organizationId=${organizationId}`
                              : `${WEBSITE_DOMAIN}/maggie/posts/org/${organizationId}`
                          }
                          title='Posts Approval'
                        />
                      </>
                    )}
                    {hasSocialMediaManagerRole(
                      session.accessTokenPayload['st-role']?.v
                    ) && (
                      <>
                        <Separator className='m-0 h-[1px] bg-muted' />
                        <ListItem
                          href={`${WEBSITE_DOMAIN}/maggie/social-calendar-management/org/${organizationId}`}
                          title='Social Calendar'
                        />
                      </>
                    )}
                    {hasAdminRole(session.accessTokenPayload['st-role']?.v) && (
                      <>
                        <Separator className='m-0 h-[1px] bg-muted' />
                        <ListItem
                          href={`${WEBSITE_DOMAIN}/maggie/writing-preferences?organizationId=${organizationId}`}
                          title='Writing Preferences'
                        />
                      </>
                    )}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenu>
          )}
        {isGrantUser &&
          hasGrantManagerRole(session.accessTokenPayload['st-role']?.v) && (
            <NavigationMenu>
              <NavigationMenuItem>
                <NavigationMenuTrigger
                  className='text-base text-gray-settings hover:text-foreground focus:bg-white focus:text-gray-settings'
                  hideIcon={true}
                  onPointerMove={(event) => event.preventDefault()}
                  onPointerLeave={(event) => event.preventDefault()}
                >
                  Grants
                </NavigationMenuTrigger>
                <NavigationMenuContent
                  onPointerEnter={(event) => event.preventDefault()}
                  onPointerLeave={(event) => event.preventDefault()}
                >
                  <ul className='flex flex-col'>
                    <ListItem
                      href={
                        isGrantDashboardEnabled || isGrantDashboardVeeEnabled
                          ? `/grant?organizationId=${organizationId}`
                          : `/grant/opportunities?organizationId=${organizationId}`
                      }
                      title='Opportunities'
                    />
                    {hasAdminRole(session.accessTokenPayload['st-role']?.v) && (
                      <ListItem
                        href={`/grant/grants?organizationId=${organizationId}`}
                        title='Grants DB operations'
                      />
                    )}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenu>
          )}
        {hasAdminRole(session.accessTokenPayload['st-role']?.v) && (
          <NavigationMenu>
            <NavigationMenuItem>
              <NavigationMenuTrigger
                className='text-base text-gray-settings hover:text-foreground focus:bg-white focus:text-gray-settings'
                hideIcon={true}
                onPointerMove={(event) => event.preventDefault()}
                onPointerLeave={(event) => event.preventDefault()}
              >
                Vee
              </NavigationMenuTrigger>
              <NavigationMenuContent
                onPointerEnter={(event) => event.preventDefault()}
                onPointerLeave={(event) => event.preventDefault()}
              >
                <ul className='flex flex-col'>
                  <ListItem
                    href={`${WEBSITE_DOMAIN}/maggie/settings?orgId=${organizationId}`}
                    title='Settings'
                  />
                  <ListItem
                    href={`${WEBSITE_DOMAIN}/onboarding`}
                    title='Onboarding'
                  />
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
          </NavigationMenu>
        )}
      </NavigationMenuList>
    </NavigationMenu>
  );
}
